import { useEffect, useRef } from 'react';
import { Bold, Italic, Underline, Eraser } from 'lucide-react';

const FONTS = [
  { label: 'Default', value: '' },
  { label: 'Sans Serif', value: 'Arial, sans-serif' },
  { label: 'Serif', value: 'Georgia, serif' },
  { label: 'Mono', value: 'Courier New, monospace' },
  { label: 'Impact', value: 'Impact, sans-serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
];

const SIZES = [
  { label: 'XS', value: '1' },
  { label: 'S', value: '2' },
  { label: 'M', value: '3' },
  { label: 'L', value: '5' },
  { label: 'XL', value: '6' },
  { label: 'XXL', value: '7' },
];

export default function RichTextEditor({ value, onChange, testIdPrefix = 'rte', minHeight = 120 }) {
  const editorRef = useRef(null);
  const initializedRef = useRef(false);

  // Initialize once per mount with incoming value
  useEffect(() => {
    if (editorRef.current && !initializedRef.current) {
      editorRef.current.innerHTML = value || '';
      initializedRef.current = true;
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(cmd, false, arg);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const handleInput = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  return (
    <div className="border border-zinc-800 bg-black rounded-none" data-testid={`${testIdPrefix}-root`}>
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-zinc-800 bg-zinc-950">
        <select
          onChange={(e) => exec('fontName', e.target.value)}
          className="bg-zinc-900 text-zinc-300 text-xs px-2 py-1 border border-zinc-800 rounded-none focus:outline-none"
          data-testid={`${testIdPrefix}-font`}
          defaultValue=""
        >
          {FONTS.map((f) => (
            <option key={f.label} value={f.value}>{f.label}</option>
          ))}
        </select>
        <select
          onChange={(e) => exec('fontSize', e.target.value)}
          className="bg-zinc-900 text-zinc-300 text-xs px-2 py-1 border border-zinc-800 rounded-none focus:outline-none"
          data-testid={`${testIdPrefix}-size`}
          defaultValue="3"
        >
          {SIZES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <label className="flex items-center gap-1 cursor-pointer" title="Text color">
          <span className="text-[10px] uppercase tracking-wide text-zinc-500">Color</span>
          <input
            type="color"
            onChange={(e) => exec('foreColor', e.target.value)}
            className="w-7 h-7 bg-transparent border border-zinc-800 cursor-pointer p-0"
            data-testid={`${testIdPrefix}-color`}
          />
        </label>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}
          className="px-2 py-1 border border-zinc-800 text-zinc-300 hover:bg-zinc-800"
          data-testid={`${testIdPrefix}-bold`}
          title="Bold"
        >
          <Bold className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}
          className="px-2 py-1 border border-zinc-800 text-zinc-300 hover:bg-zinc-800"
          data-testid={`${testIdPrefix}-italic`}
          title="Italic"
        >
          <Italic className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec('underline'); }}
          className="px-2 py-1 border border-zinc-800 text-zinc-300 hover:bg-zinc-800"
          data-testid={`${testIdPrefix}-underline`}
          title="Underline"
        >
          <Underline className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); exec('removeFormat'); }}
          className="px-2 py-1 border border-zinc-800 text-zinc-400 hover:bg-zinc-800"
          data-testid={`${testIdPrefix}-clear`}
          title="Clear formatting"
        >
          <Eraser className="w-3.5 h-3.5" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        className="p-3 text-white text-sm focus:outline-none rte-editor"
        style={{ minHeight: `${minHeight}px` }}
        data-testid={`${testIdPrefix}-content`}
      />
    </div>
  );
}
